import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';

// Внутреннее значение enum `holidays.kind` остаётся `holiday` (миграция
// БД не нужна), но в UI «праздник» → «выходной» — чтобы охватывать и
// госпраздники, и плановые выходные одной формулировкой.
const KIND_META = {
  holiday:    { label: 'Выходной',     short: 'выходной',     color: 'cal-day--holiday' },
  preholiday: { label: 'Сокр. день',   short: 'сокр.',        color: 'cal-day--preholiday' },
  vacation:   { label: 'Каникулы',     short: 'каникулы',     color: 'cal-day--vacation' },
  workday:    { label: 'Рабочий день', short: 'рабочий',      color: 'cal-day--workday' },
};

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const DOW = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const URL_SCHEMA = { year: new Date().getFullYear() };

// «пн 22.02» — для подписей переноса выходного.
function fmtRuShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const dow = DOW[(d.getDay() + 6) % 7];
  return `${dow} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function HolidaysPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('holidays', 'write');
  const canDelete = can('holidays', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const year = Number(params.year) || new Date().getFullYear();

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // объект {date, kind, label, comment} или null
  const [importOpen, setImportOpen] = useState(false);

  const reload = () => {
    setLoading(true); setError(null);
    api.get(`/api/holidays?year=${year}`)
      .then((res) => setDays(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [year]);

  // index by ISO date for быстрого lookup
  const byDate = useMemo(() => {
    const m = new Map();
    for (const d of days) m.set(d.date, d);
    return m;
  }, [days]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Производственный календарь']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="cal-page">
        <div className="cal-toolbar">
          <span className="cal-toolbar__title">Год</span>
          <button className="cal-toolbar__yearbtn" onClick={() => setParams({ year: year - 1 })} title="Предыдущий год">
            <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
              <Icon name="chevron" size={11} />
            </span>
          </button>
          <span className="cal-toolbar__year">{year}</span>
          <button className="cal-toolbar__yearbtn" onClick={() => setParams({ year: year + 1 })} title="Следующий год">
            <Icon name="chevron" size={11} />
          </button>
          <span style={{ marginLeft: 12, color: 'var(--fg-muted)', fontSize: 12 }}>
            {loading ? 'загрузка…' : `записей: ${days.length}`}
          </span>
          <div className="cal-toolbar__spacer" />
          {canWrite && (
            <>
              <button className="btn btn--sm" onClick={() => setImportOpen(true)}>
                <Icon name="download" size={13} /> Импорт JSON
              </button>
              <button
                className="btn btn--primary btn--sm"
                onClick={() => setEditing({ date: '', kind: 'holiday', label: '', comment: '' })}
              >
                <Icon name="plus" size={13} /> Добавить день
              </button>
            </>
          )}
        </div>

        <div className="cal-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 12 }}>Ошибка: {error}</div>}

          <div className="cal-legend">
            {Object.entries(KIND_META).map(([k, m]) => (
              <span key={k} className="cal-legend__item">
                <span className={`cal-legend__dot ${m.color}`} />
                {m.label}
              </span>
            ))}
            <span className="cal-legend__item" style={{ marginLeft: 12 }}>
              <span className="cal-legend__dot" style={{ boxShadow: 'inset 0 0 0 1.5px var(--green-600)' }} />
              сегодня
            </span>
          </div>

          <div className="cal-grid">
            {MONTHS.map((mname, mi) => (
              <MonthGrid
                key={mi}
                year={year}
                month={mi}
                title={mname}
                byDate={byDate}
                today={today}
                onClickDay={(date) => {
                  if (!canWrite) return;
                  const existing = byDate.get(date);
                  setEditing(existing
                    ? { ...existing }
                    : { date, kind: 'holiday', label: '', comment: '' }
                  );
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <DayEditModal
          day={editing}
          isExisting={byDate.has(editing.date)}
          canDelete={canDelete}
          byDate={byDate}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            setDays((prev) => {
              const idx = prev.findIndex((x) => x.date === d.date);
              if (idx >= 0) { const next = prev.slice(); next[idx] = d; return next; }
              return [...prev, d].sort((a, b) => a.date.localeCompare(b.date));
            });
          }}
          onDeleted={(date) => setDays((prev) => prev.filter((x) => x.date !== date))}
        />
      )}

      {importOpen && (
        <ImportJsonModal
          year={year}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); reload(); }}
        />
      )}
    </>
  );
}

function MonthGrid({ year, month, title, byDate, today, onClickDay }) {
  // ISO-номер первой недели: Mon=0..Sun=6
  const firstDow = (() => {
    const d = new Date(year, month, 1);
    const js = d.getDay(); // 0=Sun..6=Sat
    return js === 0 ? 6 : js - 1;
  })();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="cal-month">
      <div className="cal-month__head">{title} {year}</div>
      <div className="cal-month__grid">
        {DOW.map((d) => <div key={d} className="cal-month__dow">{d}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="cal-day is-empty" />;
          const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const meta = byDate.get(iso);
          const dow = (i % 7);
          const isWeekend = dow === 5 || dow === 6;
          const cls = ['cal-day'];
          if (isWeekend) cls.push('is-weekend');
          if (meta) cls.push('is-special', KIND_META[meta.kind]?.color || '');
          if (iso === today) cls.push('is-today');
          return (
            <div
              key={i}
              className={cls.join(' ')}
              onClick={() => onClickDay(iso)}
              title={meta
                ? `${KIND_META[meta.kind]?.label}`
                  + (meta.kind === 'workday' && meta.transferFrom ? ` · по расписанию ${fmtRuShort(meta.transferFrom)}` : '')
                  + (meta.label ? ' · ' + meta.label : '')
                : iso}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayEditModal({ day, isExisting, canDelete, byDate, onClose, onSaved, onDeleted }) {
  const [date, setDate] = useState(day.date || '');
  const [kind, setKind] = useState(day.kind || 'holiday');
  const [label, setLabel] = useState(day.label || '');
  const [comment, setComment] = useState(day.comment || '');
  const [transferFrom, setTransferFrom] = useState(day.transferFrom || '');
  const [makeCounterpart, setMakeCounterpart] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Встречный выходной предлагаем, только если на дате-источнике
  // ещё нет записи (иначе покажем, что там уже есть).
  const counterpartExisting = transferFrom ? byDate.get(transferFrom) : null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const tf = kind === 'workday' ? transferFrom : '';
      if (tf && tf === date) {
        throw new Error('Дата-источник переноса не может совпадать с самой датой');
      }
      const payload = { date, kind, label: label.trim(), comment: comment.trim() };
      if (tf) payload.transferFrom = tf;
      const url = isExisting && day.date === date
        ? `/api/holidays/${date}`
        : '/api/holidays';
      const method = isExisting && day.date === date ? 'put' : 'post';
      const res = await api[method](url, payload);
      onSaved?.(res.day);
      // Встречная запись: выходной на дату-источник, иначе день
      // «отработается» дважды. Ошибка здесь не критична — запись
      // можно создать и вручную.
      if (tf && makeCounterpart && !counterpartExisting) {
        try {
          const res2 = await api.post('/api/holidays', {
            date: tf,
            kind: 'holiday',
            label: `Перенос выходного на ${fmtRuShort(date)}`,
            comment: '',
          });
          onSaved?.(res2.day);
        } catch { /* не блокируем сохранение основного дня */ }
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!confirm(`Удалить запись на ${date}?`)) return;
    setSaving(true); setError(null);
    try {
      await api.delete(`/api/holidays/${day.date}`);
      onDeleted?.(day.date);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isExisting ? 'Редактирование дня' : 'Добавить день'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Дата *</label>
              <input
                className="sd-edit__input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                disabled={isExisting}
              />
              {isExisting && <div className="sd-edit__hint">Дату нельзя изменить — удалите запись и создайте новую.</div>}
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Тип *</label>
              <select className="sd-edit__select" value={kind} onChange={(e) => setKind(e.target.value)} required>
                {Object.entries(KIND_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </div>
            {kind === 'workday' && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Перенос выходного: работает по расписанию</label>
                <input
                  className="sd-edit__input"
                  type="date"
                  value={transferFrom}
                  onChange={(e) => setTransferFrom(e.target.value)}
                />
                <div className="sd-edit__hint">
                  Если это рабочая суббота «за» другой день — укажите дату, по расписанию
                  которой идут занятия (например, 20.02.2027 работает за пн 22.02.2027 —
                  укажите 2027-02-22). Для обычного рабочего дня оставьте пустым.
                </div>
                {transferFrom && !counterpartExisting && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={makeCounterpart}
                      onChange={(e) => setMakeCounterpart(e.target.checked)}
                    />
                    создать встречный выходной на {fmtRuShort(transferFrom)}
                  </label>
                )}
                {transferFrom && counterpartExisting && (
                  <div className="sd-edit__hint">
                    На {fmtRuShort(transferFrom)} уже есть запись:{' '}
                    {KIND_META[counterpartExisting.kind]?.label || counterpartExisting.kind}.
                  </div>
                )}
              </div>
            )}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Подпись</label>
              <input
                className="sd-edit__input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="например, «Новогодние праздники»"
              />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Комментарий</label>
              <input
                className="sd-edit__input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>
          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {isExisting && canDelete && (
              <button type="button" className="btn btn--sm btn--danger" onClick={onDelete} disabled={saving}>
                Удалить
              </button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving || !date}>
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportJsonModal({ year, onClose, onImported }) {
  const [text, setText] = useState('');
  const [yearImport, setYearImport] = useState(year);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setText(await f.text());
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      let parsed;
      try { parsed = JSON.parse(text); }
      catch { throw new Error('Невалидный JSON'); }
      const payload = { year: Number(yearImport), ...parsed };
      const res = await api.post('/api/holidays/import', payload);
      alert(`Импортировано ${res.inserted} записей за ${res.year}. Удалено старых: ${res.deleted}.`);
      onImported?.(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось импортировать'));
    } finally { setSaving(false); }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Импорт календаря (JSON)</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Год *</label>
              <input
                className="sd-edit__input"
                type="number"
                min="1900" max="2100"
                value={yearImport}
                onChange={(e) => setYearImport(e.target.value)}
                required
              />
            </div>
            <div className="sd-edit__field"></div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">JSON</label>
              <input ref={fileRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={onFile} />
              <button type="button" className="btn btn--sm" onClick={onPickFile} style={{ alignSelf: 'flex-start' }}>
                <Icon name="download" size={13} /> Загрузить файл .json
              </button>
              <textarea
                style={{
                  width: '100%', minHeight: 220, marginTop: 6,
                  fontFamily: 'var(--font-mono)', fontSize: 11.5,
                  padding: 10, border: '1px solid var(--border-strong)', borderRadius: 6,
                  background: 'var(--panel-2)', color: 'var(--fg)',
                  resize: 'vertical',
                }}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'{\n  "holidays":    ["2025-01-01", "2025-01-02", ...],\n  "preholidays": ["2025-03-07", ...],\n  "vacations":   ["2025-04-07", ...]\n}'}
              />
              <div className="sd-edit__hint">
                Поддерживается старый формат с массивами <code>holidays</code>, <code>preholidays</code>,
                <code> vacations</code>, <code>workdays</code>. Записи за указанный год полностью заменяются.
              </div>
            </div>
          </div>
          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving || !text.trim()}>
              {saving ? 'Импортируем…' : 'Импортировать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
